import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { ChevronDown } from 'lucide-react';
import { DropdownMenu, NavItemWithDropdown } from './HeaderDropdown';

const Header = () => {
  const location = useLocation();
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRef = useRef(null);
  const hoverTimeoutRef = useRef(null);
  
  const isActive = (path) => location.pathname === path;

  // Wrap handlers in useCallback to satisfy exhaustive-deps
  const handleClickOutside = useCallback((event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setOpenDropdown(null);
    }
  }, []);

  const handleEscape = useCallback((event) => {
    if (event.key === 'Escape') {
      setOpenDropdown(null);
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  // Close dropdown on escape key
  useEffect(() => {
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, [handleEscape]);

  const aboutMenuItems = [
    { label: 'Our Practice', path: '/our-practice' },
    { label: 'Meet the Doctor', path: '/meet-the-doctor' }
  ];

  const servicesMenuItems = [
    { label: 'Traditional Primary Care', path: '/services/traditional-primary-care' },
    { 
      label: 'MDVIP Services', 
      path: '/services/mdvip-services',
      subItems: [
        { label: 'Annual Wellness Testing', path: '/services/mdvip-services/annual-wellness-testing' }
      ]
    },
    { 
      label: 'Wellness Services', 
      path: '/services/wellness-services',
      subItems: [
        { label: 'Bioidentical Hormone Replacement Therapy by Purepell', path: '/services/wellness-services/purepell' },
        { label: 'Aesthetic Services', path: '/services/wellness-services/aesthetic-services' },
        { label: 'Supplement support through Fullscript', path: '/services/wellness-services/fullscript' },
        { label: 'Mindfulness', path: '/services/wellness-services/mindfulness' }
      ]
    }
  ];

  const DropdownMenu = ({ items, isOpen, menuName }) => {
    const [hoveredSubmenu, setHoveredSubmenu] = React.useState(null);

    if (!isOpen) return null;

    return (
      <div 
        className="absolute top-full left-0 mt-1 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200"
        role="menu"
        aria-label={`${menuName} menu`}
      >
        {items.map((item, index) => (
          <div 
            key={item.path || `menu-item-${index}`}
            className="relative group"
            onMouseEnter={() => item.subItems && setHoveredSubmenu(item.path)}
            onMouseLeave={() => item.subItems && setHoveredSubmenu(null)}
          >
            {item.subItems ? (
              <>
                <Link
                  to={item.path}
                  className="block px-4 py-3 text-sm text-stone-700 hover:bg-stone-100 transition-colors duration-150"
                  role="menuitem"
                  onClick={() => setOpenDropdown(null)}
                >
                  <span className="flex items-center justify-between">
                    {item.label}
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                </Link>
              </>
            ) : (
              <Link
                to={item.path}
                className="block px-4 py-3 text-sm text-stone-700 hover:bg-stone-100 transition-colors duration-150 focus:bg-stone-100 focus:outline-none"
                role="menuitem"
                onClick={() => setOpenDropdown(null)}
              >
                {item.label}
              </Link>
            )}

            {/* Nested Submenu */}
            {item.subItems && hoveredSubmenu === item.path && (
              <div 
                className="absolute left-full top-0 ml-1 w-80 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50"
                role="menu"
              >
                {item.subItems.map((subItem) => (
                  <Link
                    key={subItem.path}
                    to={subItem.path}
                    className="block px-4 py-3 text-sm text-stone-700 hover:bg-stone-100 transition-colors duration-150"
                    role="menuitem"
                    onClick={() => setOpenDropdown(null)}
                  >
                    {subItem.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const NavItemWithDropdown = ({ label, items, dropdownName }) => {
    const isDropdownOpen = openDropdown === dropdownName;

    const handleMouseEnter = () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      setOpenDropdown(dropdownName);
    };

    const handleMouseLeave = () => {
      hoverTimeoutRef.current = setTimeout(() => {
        setOpenDropdown(null);
      }, 150);
    };

    return (
      <div
        className="relative"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        ref={openDropdown === dropdownName ? dropdownRef : null}
      >
        <button
          className={`flex items-center space-x-1 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
            isDropdownOpen
              ? 'bg-stone-50 text-stone-700'
              : 'text-stone-700 hover:bg-stone-50'
          }`}
          onClick={() => setOpenDropdown(isDropdownOpen ? null : dropdownName)}
          aria-haspopup="true"
          aria-expanded={isDropdownOpen}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setOpenDropdown(isDropdownOpen ? null : dropdownName);
            }
          }}
        >
          <span>{label}</span>
          <ChevronDown 
            className={`h-4 w-4 transition-transform duration-200 ${
              isDropdownOpen ? 'rotate-180' : ''
            }`}
          />
        </button>
        <DropdownMenu items={items} isOpen={isDropdownOpen} menuName={label} />
      </div>
    );
  };

  return (
    <header className="sticky top-0 z-50 glass-card-strong shadow-medium border-b border-white/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo and Practice Name */}
          <Link to="/" className="flex items-center space-x-3 group">
            <img 
              src="https://customer-assets.emergentagent.com/job_premium-med-care/artifacts/57m0otz5_CFP_Logo-removebg-preview.png"
              alt="Crofton Family Practice Logo"
              className="h-12 w-12 object-contain group-hover:scale-105 transition-transform duration-300"
              style={{ background: 'transparent' }}
            />
            <div>
              <h1 className="text-xl font-bold text-stone-900 leading-tight">
                Crofton Family Practice
              </h1>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1" role="navigation">
            <Link
              to="/"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                isActive('/')
                  ? 'bg-stone-50 text-stone-700'
                  : 'text-stone-700 hover:bg-stone-50'
              }`}
            >
              Home
            </Link>
            
            <NavItemWithDropdown 
              label="About" 
              items={aboutMenuItems} 
              dropdownName="about"
            />
            
            <NavItemWithDropdown 
              label="Services" 
              items={servicesMenuItems} 
              dropdownName="services"
            />
            
            <Link
              to="/contact"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                isActive('/contact')
                  ? 'bg-stone-50 text-stone-700'
                  : 'text-stone-700 hover:bg-stone-50'
              }`}
            >
              Contact
            </Link>
          </nav>

          {/* Join MDVIP Button */}
          <a
            href="https://www.mdvip.com/doctors/allisonwilliamsmd"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button className="bg-gradient-to-r from-orange-400 via-amber-500 to-orange-600 hover:from-orange-500 hover:via-amber-600 hover:to-orange-700 text-white font-semibold shadow-medium hover:shadow-strong transition-all duration-300 glow-slate">
              Join MDVIP
            </Button>
          </a>
        </div>

        {/* Mobile Navigation */}
        <nav className="md:hidden flex flex-col space-y-1 pb-3" role="navigation">
          <Link
            to="/"
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              isActive('/')
                ? 'bg-stone-50 text-stone-700'
                : 'text-stone-700 hover:bg-stone-50'
            }`}
          >
            Home
          </Link>

          {/* Mobile About Dropdown */}
          <div>
            <button
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                openDropdown === 'about-mobile'
                  ? 'bg-stone-50 text-stone-700'
                  : 'text-stone-700 hover:bg-stone-50'
              }`}
              onClick={() => setOpenDropdown(openDropdown === 'about-mobile' ? null : 'about-mobile')}
              aria-expanded={openDropdown === 'about-mobile'}
            >
              <span>About</span>
              <ChevronDown 
                className={`h-4 w-4 transition-transform duration-200 ${
                  openDropdown === 'about-mobile' ? 'rotate-180' : ''
                }`}
              />
            </button>
            {openDropdown === 'about-mobile' && (
              <div className="pl-4 mt-1 space-y-1">
                {aboutMenuItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="block px-3 py-2 text-sm text-stone-600 hover:text-stone-600 hover:bg-stone-50 rounded-lg transition-colors duration-150"
                    onClick={() => setOpenDropdown(null)}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Mobile Services Dropdown */}
          <div>
            <button
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                openDropdown === 'services-mobile'
                  ? 'bg-stone-50 text-stone-700'
                  : 'text-stone-700 hover:bg-stone-50'
              }`}
              onClick={() => setOpenDropdown(openDropdown === 'services-mobile' ? null : 'services-mobile')}
              aria-expanded={openDropdown === 'services-mobile'}
            >
              <span>Services</span>
              <ChevronDown 
                className={`h-4 w-4 transition-transform duration-200 ${
                  openDropdown === 'services-mobile' ? 'rotate-180' : ''
                }`}
              />
            </button>
            {openDropdown === 'services-mobile' && (
              <div className="pl-4 mt-1 space-y-1">
                {servicesMenuItems.map((item) => (
                  <div key={item.path}>
                    <Link
                      to={item.path}
                      className="block px-3 py-2 text-sm text-stone-600 hover:text-stone-600 hover:bg-stone-50 rounded-lg transition-colors duration-150"
                      onClick={() => !item.subItems && setOpenDropdown(null)}
                    >
                      {item.label}
                    </Link>
                    {item.subItems && (
                      <div className="pl-4 mt-1 space-y-1">
                        {item.subItems.map((subItem) => (
                          <Link
                            key={subItem.path}
                            to={subItem.path}
                            className="block px-3 py-2 text-xs text-stone-500 hover:text-stone-600 hover:bg-stone-50 rounded-lg transition-colors duration-150"
                            onClick={() => setOpenDropdown(null)}
                          >
                            • {subItem.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/contact"
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              isActive('/contact')
                ? 'bg-stone-50 text-stone-700'
                : 'text-stone-700 hover:bg-stone-50'
            }`}
          >
            Contact
          </Link>
        </nav>
      </div>
    </header>
  );
};

export default Header;
